import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ImageIcon, BookOpen, Palette, Calendar, Wrench, Download, Frame,
  Sparkles, Upload, ArrowRight, ArrowLeft, Loader2, Check, Eye,
  DollarSign, Tag, FileText, X, RefreshCw, ShoppingBag, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const ICONS = {
  image: ImageIcon, 'book-open': BookOpen, palette: Palette,
  calendar: Calendar, wrench: Wrench, download: Download, frame: Frame,
};

const STEPS = ['type', 'mode', 'create', 'review'];
const STEP_LABELS = ['Product Type', 'Creation Mode', 'Create', 'Review & Publish'];

export default function CreatorPage() {
  const { user } = useAuth();
  const { hasAIAccess, isOwner } = usePermissions();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [mode, setMode] = useState(null); // 'ai' | 'upload'
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // AI mode state
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('');

  // Upload mode state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);

  // Result state
  const [result, setResult] = useState(null);

  // Editable fields for publish
  const [editTitle, setEditTitle] = useState('');
  const [editSubtitle, setEditSubtitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState('');

  // Publishing state
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishedId, setPublishedId] = useState(null);

  useEffect(() => {
    api.get('/creator/types').then(r => setTypes(r.data.types)).catch(() => {});
  }, []);

  const startSession = useCallback(async (ptype, m) => {
    try {
      const res = await api.post('/creator/start', { product_type: ptype, mode: m });
      setSessionId(res.data.id);
      return res.data.id;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start session');
      return null;
    }
  }, []);

  const handleSelectType = (t) => {
    setSelectedType(t);
    setStep(1);
  };

  const handleSelectMode = async (m) => {
    setMode(m);
    setLoading(true);
    const sid = await startSession(selectedType.id, m);
    setLoading(false);
    if (sid) setStep(2);
  };

  const handleAIGenerate = async () => {
    if (!prompt.trim()) { toast.error('Please describe what you want to create'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/creator/generate', { session_id: sessionId, prompt, style });
      setResult(res.data);
      setEditTitle(res.data.title || '');
      setEditSubtitle(res.data.subtitle || '');
      setEditDescription(res.data.description || '');
      setEditPrice(String(res.data.price_suggestion || '9.99'));
      setStep(3);
      toast.success('Product generated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setUploadPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadedFile) { toast.error('Please upload a file first'); return; }
    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('file', uploadedFile);
      await api.post('/creator/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

      const res = await api.post('/creator/process-upload', { session_id: sessionId });
      setResult(res.data);
      setEditTitle(res.data.title || '');
      setEditSubtitle(res.data.subtitle || '');
      setEditDescription(res.data.description || '');
      setEditPrice(String(res.data.price_suggestion || '9.99'));
      setStep(3);
      toast.success('Product processed successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Processing failed');
    } finally { setGenerating(false); }
  };

  const handlePublish = async () => {
    if (!editTitle.trim()) { toast.error('Title is required'); return; }
    if (!editPrice || parseFloat(editPrice) <= 0) { toast.error('Please set a valid price'); return; }
    setPublishing(true);
    try {
      const res = await api.post('/creator/publish', {
        session_id: sessionId,
        title: editTitle,
        subtitle: editSubtitle,
        description: editDescription,
        price: parseFloat(editPrice),
        category: selectedType?.id || 'digital',
      });
      setPublished(true);
      setPublishedId(res.data.product_id);
      toast.success('Product published to marketplace!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publishing failed');
    } finally { setPublishing(false); }
  };

  const resetCreator = () => {
    setStep(0); setSelectedType(null); setMode(null); setSessionId(null);
    setPrompt(''); setStyle(''); setUploadedFile(null); setUploadPreview(null);
    setResult(null); setPublished(false); setPublishedId(null);
    setEditTitle(''); setEditSubtitle(''); setEditDescription(''); setEditPrice('');
  };

  // ── Step 0: Product Type Selection ──
  const renderTypeSelection = () => (
    <div className="max-w-3xl mx-auto" data-testid="creator-step-type">
      <div className="text-center mb-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian mb-2">What are you creating?</h1>
        <p className="text-sm text-muted-foreground">Choose a product type to get started</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {types.map(t => {
          const Icon = ICONS[t.icon] || FileText;
          return (
            <button
              key={t.id}
              onClick={() => handleSelectType(t)}
              data-testid={`type-${t.id}`}
              className="group border border-border/40 rounded-xl p-5 text-left hover:border-amber-300 hover:shadow-lg transition-all duration-300 bg-white"
            >
              <Icon size={22} className="text-amber-500 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-medium text-sm text-obsidian mb-1">{t.name}</h3>
              <p className="text-[10px] text-muted-foreground leading-relaxed">{t.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── Step 1: Mode Selection ──
  const renderModeSelection = () => (
    <div className="max-w-2xl mx-auto" data-testid="creator-step-mode">
      <button onClick={() => setStep(0)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-obsidian mb-6 transition-colors">
        <ArrowLeft size={14} /> Back to types
      </button>
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 border border-amber-200/60 bg-amber-50/40 px-3 py-1 rounded-full mb-4">
          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-medium">{selectedType?.name}</span>
        </div>
        <h1 className="font-serif text-3xl text-obsidian mb-2">How do you want to create?</h1>
        <p className="text-sm text-muted-foreground">Choose your creation method</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <button
          onClick={() => handleSelectMode('ai')}
          disabled={loading || (!hasAIAccess && !isOwner)}
          data-testid="mode-ai"
          className="group border-2 border-violet-200 rounded-2xl p-8 text-left hover:border-violet-400 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-violet-50/30 to-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Sparkles size={28} className="text-violet-500 mb-4" />
          <h3 className="font-serif text-lg text-obsidian mb-2">Create from AI</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Describe what you want. AI generates the product, images, mockups, copy, and pricing — all automatically.
          </p>
        </button>
        <button
          onClick={() => handleSelectMode('upload')}
          disabled={loading}
          data-testid="mode-upload"
          className="group border-2 border-amber-200 rounded-2xl p-8 text-left hover:border-amber-400 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-amber-50/30 to-white"
        >
          <Upload size={28} className="text-amber-500 mb-4" />
          <h3 className="font-serif text-lg text-obsidian mb-2">Upload Your Own</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Upload your file — wallpaper, artwork, design, or document. AI generates mockups, copy, and marketplace assets.
          </p>
        </button>
      </div>
      {loading && (
        <div className="text-center mt-6">
          <Loader2 size={20} className="animate-spin text-amber-500 mx-auto" />
        </div>
      )}
    </div>
  );

  // ── Step 2: Create (AI prompt or Upload) ──
  const renderCreateStep = () => (
    <div className="max-w-2xl mx-auto" data-testid="creator-step-create">
      <button onClick={() => { setStep(1); setMode(null); }} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-obsidian mb-6 transition-colors">
        <ArrowLeft size={14} /> Back
      </button>
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 border border-amber-200/60 bg-amber-50/40 px-3 py-1 rounded-full mb-4">
          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-medium">{selectedType?.name} — {mode === 'ai' ? 'AI Creation' : 'Upload'}</span>
        </div>
        <h1 className="font-serif text-2xl text-obsidian">
          {mode === 'ai' ? 'Describe your product' : `Upload your ${selectedType?.name?.toLowerCase()}`}
        </h1>
      </div>

      {mode === 'ai' ? (
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">What do you want to create?</Label>
            <Textarea
              data-testid="ai-prompt"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder={`Describe your ${selectedType?.name?.toLowerCase()} in detail — topic, style, audience, mood...`}
              className="min-h-[120px] rounded-xl border-border/60 focus:border-violet-400 resize-none"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Style preference (optional)</Label>
            <Input
              data-testid="ai-style"
              value={style}
              onChange={e => setStyle(e.target.value)}
              placeholder="e.g. minimalist, luxury, playful, corporate, artistic..."
              className="rounded-xl border-border/60 focus:border-violet-400"
            />
          </div>
          <Button
            onClick={handleAIGenerate}
            disabled={generating || !prompt.trim()}
            data-testid="generate-btn"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-sm uppercase tracking-wider disabled:opacity-40"
          >
            {generating ? (
              <><Loader2 size={16} className="animate-spin mr-2" /> Generating your product...</>
            ) : (
              <><Sparkles size={16} className="mr-2" /> Generate with AI</>
            )}
          </Button>
          {generating && (
            <p className="text-center text-[11px] text-muted-foreground animate-pulse">
              Creating your {selectedType?.name?.toLowerCase()} — this may take 30-60 seconds...
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          <div
            className="border-2 border-dashed border-amber-200 rounded-2xl p-10 text-center hover:border-amber-400 transition-colors cursor-pointer relative"
            onClick={() => document.getElementById('file-upload').click()}
            data-testid="upload-zone"
          >
            {uploadPreview ? (
              <div className="relative">
                <img src={uploadPreview} alt="Preview" className="max-h-64 mx-auto rounded-lg shadow-lg" />
                <button
                  onClick={e => { e.stopPropagation(); setUploadedFile(null); setUploadPreview(null); }}
                  className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow"
                >
                  <X size={14} />
                </button>
              </div>
            ) : uploadedFile ? (
              <div>
                <FileText size={40} className="mx-auto text-amber-400 mb-3" />
                <p className="text-sm font-medium text-obsidian">{uploadedFile.name}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            ) : (
              <>
                <Upload size={40} className="mx-auto text-amber-300 mb-3" />
                <p className="text-sm text-obsidian font-medium mb-1">
                  Upload your {selectedType?.name?.toLowerCase()} here
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Drop your file or click to browse — images, PDFs, design files
                </p>
              </>
            )}
            <input id="file-upload" type="file" className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.zip,.epub" />
          </div>

          {/* Creator Protection Notice */}
          <div className="flex gap-3 p-4 bg-emerald-50/60 border border-emerald-200/50 rounded-xl" data-testid="creator-protection-notice">
            <ShieldCheck size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-emerald-800">Creator Protection Enabled</p>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                <span className="text-[10px] text-emerald-700/80">Secure File Delivery</span>
                <span className="text-[10px] text-emerald-700/80">Protected Downloads</span>
                <span className="text-[10px] text-emerald-700/80">Full Creator Ownership</span>
              </div>
            </div>
          </div>

          <Button
            onClick={handleUploadSubmit}
            disabled={generating || !uploadedFile}
            data-testid="upload-submit-btn"
            className="w-full h-12 rounded-xl bg-obsidian hover:bg-amber-600 text-white text-sm uppercase tracking-wider disabled:opacity-40"
          >
            {generating ? (
              <><Loader2 size={16} className="animate-spin mr-2" /> Processing your file...</>
            ) : (
              <><ArrowRight size={16} className="mr-2" /> Process & Generate Assets</>
            )}
          </Button>
        </div>
      )}
    </div>
  );

  // ── Step 3: Review & Publish ──
  const renderReview = () => {
    if (published) {
      return (
        <div className="max-w-lg mx-auto text-center py-16" data-testid="creator-published">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} className="text-emerald-600" />
          </div>
          <h1 className="font-serif text-3xl text-obsidian mb-3">Published!</h1>
          <p className="text-sm text-muted-foreground mb-8">Your product is now live on the marketplace.</p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={() => navigate(`/products/${publishedId}`)} className="rounded-xl h-11 bg-obsidian hover:bg-amber-600 text-white text-xs uppercase tracking-wider" data-testid="view-product-btn">
              <Eye size={14} className="mr-1.5" /> View Product
            </Button>
            <Button onClick={resetCreator} variant="outline" className="rounded-xl h-11 text-xs uppercase tracking-wider" data-testid="create-another-btn">
              <RefreshCw size={14} className="mr-1.5" /> Create Another
            </Button>
          </div>
        </div>
      );
    }

    const isVisual = selectedType?.category === 'visual';

    return (
      <div className="max-w-4xl mx-auto" data-testid="creator-step-review">
        <button onClick={() => setStep(2)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-obsidian mb-6 transition-colors">
          <ArrowLeft size={14} /> Back to edit
        </button>
        <div className="text-center mb-8">
          <h1 className="font-serif text-2xl text-obsidian mb-1">Review your product</h1>
          <p className="text-xs text-muted-foreground">Check everything below, edit what you need, then publish.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Product Preview */}
          <div className="lg:col-span-3 space-y-5">

            {/* Room Mockups (PRIMARY for wallpaper/artwork) */}
            {result?.room_mockups?.length > 0 && (
              <div data-testid="review-room-mockups">
                <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3 font-medium">Marketplace Gallery Preview</h3>
                <div className="rounded-2xl overflow-hidden border border-border/30 shadow-lg mb-3">
                  <img src={result.room_mockups[0].url} alt={result.room_mockups[0].label} className="w-full object-cover" />
                </div>
                {result.room_mockups.length > 1 && (
                  <div className="grid grid-cols-2 gap-3">
                    {result.room_mockups.slice(1).map((rm, i) => (
                      <div key={i} className="rounded-xl overflow-hidden border border-border/20 shadow-sm">
                        <img src={rm.url} alt={rm.label} className="w-full object-cover" />
                        <div className="bg-stone-50 px-3 py-1.5">
                          <p className="text-[10px] text-muted-foreground">{rm.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Cover (for non-visual products) */}
            {!isVisual && result?.cover_image && (
              <div className="rounded-2xl overflow-hidden border border-border/30 shadow-lg" data-testid="review-cover">
                <img src={result.cover_image} alt="Product" className="w-full object-cover max-h-[500px]" />
              </div>
            )}

            {/* Raw file preview (collapsible for visual products) */}
            {isVisual && result?.raw_image && (
              <div data-testid="review-raw-file">
                <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3 font-medium">Original File (Download Asset)</h3>
                <div className="rounded-xl overflow-hidden border border-amber-200/50 shadow-sm bg-stone-50 p-2">
                  <img src={result.raw_image} alt="Original file" className="w-full object-contain max-h-[250px] rounded-lg" />
                  <p className="text-[9px] text-center text-amber-600 mt-2">This is the downloadable file — not shown as main marketplace image</p>
                </div>
              </div>
            )}

            {/* Device Mockups */}
            {result?.mockups?.length > 0 && (
              <div data-testid="review-mockups">
                <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3 font-medium">Device Previews</h3>
                <div className="grid grid-cols-3 gap-3">
                  {result.mockups.map((m, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-border/20 shadow-sm">
                      <img src={m.url} alt={m.type || `Mockup ${i+1}`} className="w-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Previews */}
            {result?.preview_images?.length > 0 && (
              <div data-testid="review-previews">
                <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3 font-medium">Social Previews</h3>
                <div className="grid grid-cols-2 gap-3">
                  {result.preview_images.map((p, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-border/20 shadow-sm">
                      <img src={p.url} alt={p.platform || `Preview ${i+1}`} className="w-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Product Specifications */}
            {result?.specs && (
              <div className="border border-border/30 rounded-xl p-5 bg-stone-50/50" data-testid="review-specs">
                <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3 font-medium">Product Specifications</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {result.specs.source_resolution && <div><p className="text-[9px] text-muted-foreground">Source Resolution</p><p className="text-xs font-medium text-obsidian">{result.specs.source_resolution}</p></div>}
                  {result.specs.resolution && !result.specs.source_resolution && <div><p className="text-[9px] text-muted-foreground">Resolution</p><p className="text-xs font-medium text-obsidian">{result.specs.resolution}</p></div>}
                  {result.specs.format && <div><p className="text-[9px] text-muted-foreground">Format</p><p className="text-xs font-medium text-obsidian">{result.specs.format}</p></div>}
                  {result.specs.dpi_range && <div><p className="text-[9px] text-muted-foreground">Print Quality</p><p className="text-xs font-medium text-obsidian">{result.specs.dpi_range}</p></div>}
                  {result.specs.dpi && !result.specs.dpi_range && <div><p className="text-[9px] text-muted-foreground">Print Quality</p><p className="text-xs font-medium text-obsidian">{result.specs.dpi}</p></div>}
                  {result.specs.coverage_sizes && <div className="col-span-2"><p className="text-[9px] text-muted-foreground">Coverage Sizes</p><p className="text-xs font-medium text-obsidian">{result.specs.coverage_sizes}</p></div>}
                  {result.specs.suitable_for && <div className="col-span-2"><p className="text-[9px] text-muted-foreground">Suitable For</p><p className="text-xs font-medium text-obsidian">{result.specs.suitable_for}</p></div>}
                  {result.specs.delivery && <div className="col-span-2"><p className="text-[9px] text-muted-foreground">Delivery</p><p className="text-xs font-medium text-obsidian">{result.specs.delivery}</p></div>}
                  {result.specs.tiling_compatibility && <div className="col-span-2"><p className="text-[9px] text-muted-foreground">Tiling</p><p className="text-xs font-medium text-obsidian">{result.specs.tiling_compatibility}</p></div>}
                  {result.specs.installation_note && <div className="col-span-2"><p className="text-[9px] text-muted-foreground">Installation</p><p className="text-xs font-medium text-obsidian">{result.specs.installation_note}</p></div>}
                </div>

                {/* Wall-size variants */}
                {result?.wall_variants?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-border/20" data-testid="review-wall-variants">
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-2 font-medium">Included Wall Sizes ({result.wall_variants.length} files)</p>
                    <div className="grid grid-cols-2 gap-2">
                      {result.wall_variants.map((wv, i) => (
                        <div key={i} className="bg-white rounded-lg border border-border/20 p-2.5">
                          <p className="text-[10px] font-medium text-obsidian">{wv.label}</p>
                          <p className="text-[9px] text-muted-foreground">{wv.coverage} · {wv.resolution}</p>
                          <p className="text-[9px] text-amber-600">{wv.dpi} DPI · {wv.file_size}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Product file confirmation */}
            {result?.product_file_url && (
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="review-file-ready">
                <Check size={18} className="text-emerald-600 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-emerald-800">
                    {result?.wall_variants?.length > 0 
                      ? `${result.wall_variants.length + 1} files included (source + ${result.wall_variants.length} wall sizes)` 
                      : 'Downloadable file attached'}
                  </p>
                  <p className="text-[10px] text-emerald-600">Buyers will receive all files after purchase</p>
                </div>
              </div>
            )}
          </div>

          {/* Right: Editable Fields */}
          <div className="lg:col-span-2 space-y-4">
            {/* Creator Protection Notice */}
            <div className="flex gap-3 p-4 bg-emerald-50/60 border border-emerald-200/50 rounded-xl" data-testid="review-protection-notice">
              <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-medium text-emerald-800">Creator Protection Enabled</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  <span className="text-[9px] text-emerald-700/80">Secure File Delivery</span>
                  <span className="text-[9px] text-emerald-700/80">Protected Downloads</span>
                  <span className="text-[9px] text-emerald-700/80">Full Creator Ownership</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-border/30 rounded-2xl p-6 space-y-4 shadow-sm">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Title</Label>
                <Input
                  data-testid="edit-title"
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  className="mt-1 rounded-lg border-border/60 font-medium"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Subtitle</Label>
                <Input
                  data-testid="edit-subtitle"
                  value={editSubtitle}
                  onChange={e => setEditSubtitle(e.target.value)}
                  className="mt-1 rounded-lg border-border/60"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</Label>
                <Textarea
                  data-testid="edit-description"
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  className="mt-1 rounded-lg border-border/60 min-h-[120px] resize-none"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <DollarSign size={12} /> Price (EUR)
                </Label>
                <Input
                  data-testid="edit-price"
                  type="number"
                  min="0.50"
                  step="0.01"
                  value={editPrice}
                  onChange={e => setEditPrice(e.target.value)}
                  className="mt-1 rounded-lg border-border/60 font-serif text-lg"
                />
              </div>
              {result?.sales_copy && (
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-medium">Sales Copy</p>
                  <p className="text-xs text-obsidian/80 italic">{result.sales_copy}</p>
                </div>
              )}
            </div>

            <Button
              onClick={handlePublish}
              disabled={publishing || !editTitle.trim() || !editPrice}
              data-testid="publish-btn"
              className="w-full h-12 rounded-xl bg-obsidian hover:bg-amber-600 text-white text-sm uppercase tracking-wider transition-colors duration-300 disabled:opacity-40"
            >
              {publishing ? (
                <><Loader2 size={16} className="animate-spin mr-2" /> Publishing...</>
              ) : (
                <><ShoppingBag size={16} className="mr-2" /> Publish to Marketplace</>
              )}
            </Button>
            <p className="text-[10px] text-center text-muted-foreground">
              Review everything above before publishing. You can edit all fields.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // ── Progress indicator ──
  const renderProgress = () => (
    <div className="flex items-center justify-center gap-2 mb-10" data-testid="creator-progress">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold transition-all ${
            i < step ? 'bg-amber-500 text-white' : i === step ? 'bg-obsidian text-white' : 'bg-stone-100 text-stone-400'
          }`}>
            {i < step ? <Check size={12} /> : i + 1}
          </div>
          <span className={`text-[10px] uppercase tracking-wider hidden sm:inline ${
            i === step ? 'text-obsidian font-semibold' : 'text-muted-foreground'
          }`}>{STEP_LABELS[i]}</span>
          {i < STEPS.length - 1 && <div className={`w-8 h-px ${i < step ? 'bg-amber-400' : 'bg-stone-200'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8" data-testid="creator-page">
      {!published && renderProgress()}
      {step === 0 && renderTypeSelection()}
      {step === 1 && renderModeSelection()}
      {step === 2 && renderCreateStep()}
      {step === 3 && renderReview()}
    </div>
  );
}
